import { useState, useRef, useEffect, type MouseEvent } from 'react';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useFloating,
  type Placement,
} from '@floating-ui/react';
import { FileDown, Check, Plus } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useCartActions } from '../../hooks/useCartActions';
import { useTranslation } from '../../i18n';
import { useToast } from '../../context/ToastContext';

interface GroupCartPickerProps {
  productIds: number[];
  align?: 'left' | 'right';
  direction?: 'up' | 'down';
}

/**
 * Dropdown that lets the user pick which document (cart) a whole favorites
 * group is added to, instead of always targeting the active document.
 */
export default function GroupCartPicker({
  productIds,
  align = 'right',
  direction = 'down',
}: GroupCartPickerProps) {
  const { t } = useTranslation();
  const { isLoggedIn } = useAuth();
  const { carts, createCart, addAllToSpecificCart, removeAllFromSpecificCart } = useCartActions(isLoggedIn);
  const toast = useToast();

  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCartName, setNewCartName] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const placement: Placement = `${direction === 'down' ? 'bottom' : 'top'}-${
    align === 'right' ? 'end' : 'start'
  }`;

  const { refs: floatingRefs, floatingStyles } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    if (!open) return;
    const handler = (e: globalThis.MouseEvent) => {
      const target = e.target as Node;
      const clickedTrigger = ref.current?.contains(target);
      const clickedMenu = floatingRefs.floating.current?.contains(target);
      if (!clickedTrigger && !clickedMenu) {
        setOpen(false);
        setCreating(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, floatingRefs]);

  const missingInCart = (cartId: number): number[] => {
    const cartProducts = carts.find((c) => c.id === cartId)?.products ?? [];
    const present = new Set(cartProducts.map((p) => p.id));
    return productIds.filter((id) => !present.has(id));
  };

  const presentInCart = (cartId: number): number[] => {
    const cartProducts = carts.find((c) => c.id === cartId)?.products ?? [];
    const present = new Set(cartProducts.map((p) => p.id));
    return productIds.filter((id) => present.has(id));
  };

  const inAnyCart = carts.some((c) => presentInCart(c.id).length > 0);

  const handleButtonClick = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(!open);
  };

  const handleCartSelect = (cartId: number) => {
    const missing = missingInCart(cartId);
    setOpen(false);
    setCreating(false);
    if (missing.length === 0) {
      // All group products already in this document → toggle them out.
      removeAllFromSpecificCart(cartId, productIds);
      toast.show(t.favorites.removedFromDocument, 'success');
      return;
    }
    addAllToSpecificCart(cartId, missing);
    toast.show(t.favorites.addedToDocument, 'success');
  };

  const handleRemoveFromAll = () => {
    setOpen(false);
    setCreating(false);
    for (const cart of carts) {
      const present = presentInCart(cart.id);
      if (present.length > 0) {
        removeAllFromSpecificCart(cart.id, present);
      }
    }
    toast.show(t.favorites.removedFromDocument, 'success');
  };

  const handleCreateCart = () => {
    const trimmed = newCartName.trim();
    if (!trimmed) return;
    createCart(trimmed, productIds);
    setNewCartName('');
    setCreating(false);
    setOpen(false);
    toast.show(t.favorites.addedToDocument, 'success');
  };

  return (
    <div className="relative" ref={ref}>
      <button
        ref={floatingRefs.setReference}
        type="button"
        title={t.favorites.addGroupToDocument}
        aria-label={t.favorites.addGroupToDocument}
        className="inline-flex items-center justify-center whitespace-nowrap rounded-full font-medium transition-colors duration-150 cursor-pointer bg-vivatis-600 text-white hover:bg-vivatis-700 active:bg-vivatis-800 px-3 py-1.5 text-sm gap-1.5"
        onClick={handleButtonClick}
      >
        <span className="shrink-0">
          <FileDown className="w-3.5 h-3.5" />
        </span>
        <span className="hidden sm:inline">{t.favorites.addGroupToDocument}</span>
      </button>

      {open && (
        <FloatingPortal>
          <div
            // eslint-disable-next-line react-hooks/refs
            ref={floatingRefs.setFloating}
            style={floatingStyles}
            className="z-[60] w-52 rounded-xl bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 py-1"
          >
            {inAnyCart && (
              <>
                <button
                  type="button"
                  className="w-full text-left px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFromAll();
                  }}
                >
                  {t.actions.removeFromAllCarts}
                </button>
                <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
              </>
            )}
            <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 py-1">
              {t.cart.title}
            </p>
            {carts.map((cart) => {
              const allInThisCart = missingInCart(cart.id).length === 0;
              return (
                <button
                  key={cart.id}
                  type="button"
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors cursor-pointer ${
                    allInThisCart
                      ? 'bg-vivatis-50 text-vivatis-700 dark:bg-vivatis-900/20 dark:text-vivatis-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCartSelect(cart.id);
                  }}
                >
                  {allInThisCart ? (
                    <Check className="w-3.5 h-3.5 shrink-0 text-vivatis-500" />
                  ) : (
                    <span className="w-3.5 shrink-0" />
                  )}
                  <span className="truncate flex-1">{cart.name}</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500">{(cart.products ?? []).length}</span>
                </button>
              );
            })}

            <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
            {creating ? (
              <form
                className="px-3 py-1.5 flex gap-1"
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCreateCart();
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="text"
                  value={newCartName}
                  onChange={(e) => setNewCartName(e.target.value)}
                  placeholder={t.cart.newDocument}
                  className="flex-1 min-w-0 text-sm px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-vivatis-500"
                  autoFocus
                />
                <button
                  type="submit"
                  className="shrink-0 px-2 py-1 text-xs font-medium rounded bg-vivatis-600 text-white hover:bg-vivatis-700"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </form>
            ) : (
              <button
                type="button"
                className="w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 text-vivatis-600 dark:text-vivatis-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setCreating(true);
                }}
              >
                <Plus className="w-3.5 h-3.5" />
                <span>{t.cart.newDocument}</span>
              </button>
            )}
          </div>
        </FloatingPortal>
      )}
    </div>
  );
}
